import {
  getAdminChartsIcon,
  getAdminRequestsIcon,
  getAnalyticsIcon,
  getReportSettingsIcon,
  getRequestsIcon,
  getSalesConsultantIcon,
  getSupportIcon,
  getSystemSettingsIcon,
  getTicketsIcon,
  getTicketsIconWithPath,
  getUserManagementIcon,
} from "@/assets/drawerIcons";

/*
  Declare a link on route.meta in any role router file.

  Main link (no submenu):
    listed: true
    page_name: "الاسم"
    icon: `<svg>...</svg>`
    menuSort: 15          // same line as groups. 5 is above group 10. Omit to stay after groups.

  Submenu link:
    listed: true
    page_name: "الاسم"
    group: "الطلبات"      // exact name, spaces matter
    menuSort: 10          // order inside the group only
    groupSort: 15         // moves the whole group. Set on one child.
    groupIcon: `<svg>...</svg>`  // optional, first child that sets it wins

  A new group name stays hidden until one child sets groupSort,
  except admin, where a new group is shown at the end.
  "طلبات الدعم الفني" stays pinned at the bottom (not admin). menuSort still orders its children.
*/

const BOTTOM_GROUP = "طلبات الدعم الفني";

const CS_CHILD_HIDDEN = new Set([
  "/agent/requests/completed",
  "/agent/requests/all",
  "/agent/requests/recieved",
  "/agent/requests/follow",
  "/agent/requests/archieved",
]);

const FOREIGN_AREAS = new Set([
  "admin",
  "salesManager",
  "funding-manager",
  "accounting",
  "quality",
]);

function asNumber(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function bySort(left, right) {
  return left.sort - right.sort || left.order - right.order;
}

function area(route) {
  return (route.path || "").split("/")[1] || "";
}

function segment(route, index) {
  return (route.path || "").split("/")[index] || "";
}

function groupOf(route) {
  const group = route.meta && route.meta.group;
  return typeof group === "string" ? group : "";
}

function hasGroup(route) {
  return groupOf(route) !== "";
}

function csSegmentsOk(route) {
  const third = segment(route, 3);
  return third !== "special" && third !== "extra" && third !== "archieved-in-quality";
}

function notForCustomerService(route) {
  return !(route.meta && route.meta.is_for_customer_service);
}

function ticketsIcon(color) {
  return getTicketsIcon(color).replace(/\$iconColor/g, color);
}

function ticketsPathIcon(color) {
  return getTicketsIconWithPath(color).replace(/\$iconColor/g, color);
}

function roleMenus(iconColor) {
  return {
    admin: {
      showUnknownGroups: true,
      bottom: [],
      groups: [
        { name: " الطلبات", sort: 10, icon: getAdminRequestsIcon() },
        { name: "خدمة العملاء", sort: 20, icon: getAdminChartsIcon() },
        { name: "اعدادت التقارير", sort: 30, icon: getReportSettingsIcon() },
        { name: "إدارة المستحدمين", sort: 40, icon: getUserManagementIcon() },
        { name: "الرسوم البيانية", sort: 50, icon: getAdminChartsIcon() },
        { name: "التذاكـر", sort: 60, icon: ticketsPathIcon(iconColor) },
        { name: "اعدادات النظام", sort: 70, icon: getSystemSettingsIcon() },
        { name: "التقارير", sort: 80, icon: getAnalyticsIcon() },
        { name: "فريق التطوير", sort: 90, icon: getSystemSettingsIcon() },
      ],
      isLink(route) {
        return area(route) === "admin" && !hasGroup(route);
      },
      isChild(route) {
        return area(route) === "admin" && hasGroup(route);
      },
    },
    sales: {
      groups: [
        { name: "الطلبات ", sort: 10, icon: getRequestsIcon(iconColor) },
        { name: "استشاري المبيعات", sort: 20, icon: getSalesConsultantIcon() },
        { name: "التذاكر ", sort: 30, icon: ticketsPathIcon(iconColor) },
        { name: "الرسوم البيانية ", sort: 40, icon: getAnalyticsIcon() },
      ],
      isLink(route) {
        return area(route) === "salesManager" && route.meta.group === "";
      },
      isChild(route) {
        return hasGroup(route) && notForCustomerService(route);
      },
    },
    funding: {
      pinTop: true,
      groups: [{ name: "الطلبات..", sort: 10, icon: getRequestsIcon(iconColor) }],
      isLink(route) {
        return area(route) === "funding-manager" && route.meta.group === "";
      },
      isChild(route) {
        return hasGroup(route) && notForCustomerService(route);
      },
    },
    quality: {
      groups: [
        { name: "الطلبات .", sort: 10, icon: getRequestsIcon(iconColor) },
        { name: "التذاكر .", sort: 20, icon: ticketsIcon(iconColor) },
      ],
      isLink(route) {
        return area(route) === "quality" && route.meta.group === "";
      },
      isChild(route) {
        return hasGroup(route) && notForCustomerService(route);
      },
    },
    wasata: {
      groups: [{ name: "التقارير.", sort: 10, icon: getRequestsIcon(iconColor) }],
      isLink(route) {
        return area(route) === "accounting" && route.meta.group === "";
      },
      isChild(route) {
        return hasGroup(route) && notForCustomerService(route);
      },
    },
    tasaheel: {
      groups: [{ name: " التقارير", sort: 10, icon: getRequestsIcon(iconColor) }],
      isLink(route) {
        return area(route) === "accounting" && route.meta.group === "";
      },
      isChild(route) {
        return hasGroup(route) && notForCustomerService(route);
      },
    },
    supervisor: {
      groups: [{ name: "خدمة العملاء", sort: 10, icon: getAdminChartsIcon() }],
      isLink(route) {
        if (area(route) !== "customer-service-supervisor") {
          return false;
        }
        return !route.meta.group;
      },
      isChild(route) {
        return area(route) === "customer-service-supervisor" && hasGroup(route);
      },
    },
    customerService: {
      groups: [
        { name: "الطلبات", sort: 10, icon: getRequestsIcon(iconColor) },
        { name: "التذاكر", sort: 20, icon: ticketsIcon(iconColor) },
      ],
      isLink(route) {
        if (hasGroup(route) || !csSegmentsOk(route) || route.path === "/agent/requests/all") {
          return false;
        }
        if (FOREIGN_AREAS.has(area(route))) {
          return false;
        }
        const second = segment(route, 2);
        return second !== "chat" && second !== "rates";
      },
      isChild(route) {
        return hasGroup(route) && csSegmentsOk(route) && !CS_CHILD_HIDDEN.has(route.path);
      },
    },
    agent: {
      groups: [
        { name: "الطلبات", sort: 10, icon: getRequestsIcon(iconColor) },
        { name: "التذاكر", sort: 20, icon: ticketsIcon(iconColor) },
        { name: " الرسوم البيانية", sort: 30, icon: getAnalyticsIcon() },
      ],
      isLink(route) {
        if (hasGroup(route)) {
          return false;
        }
        return !FOREIGN_AREAS.has(area(route)) && area(route) !== "customer-service";
      },
      isChild(route) {
        return hasGroup(route) && notForCustomerService(route);
      },
    },
  };
}

function resolveRoleKey({ isAdmin, role, isCustomerService, accountType }) {
  if (isAdmin) {
    return "admin";
  }
  if (role === "1") {
    return "sales";
  }
  if (role === "2") {
    return "funding";
  }
  if (role === "5") {
    return "quality";
  }
  if (role === "8") {
    return String(accountType) === "1" ? "wasata" : "tasaheel";
  }
  if (role === "16") {
    return "supervisor";
  }
  if (role === "0" && isCustomerService) {
    return "customerService";
  }
  return "agent";
}

/**
 * One ordered list for the signed-in user.
 * groupSort and a main link's menuSort share one line, so a link can sit above a group.
 */
export function buildUserMenu(routes, context = {}) {
  const iconColor = context.iconColor || "#fff";
  const roleKey = resolveRoleKey(context);
  const roleMenu = roleMenus(iconColor)[roleKey];
  const defaults = new Map(roleMenu.groups.map((group) => [group.name, group]));
  const bottomNames = roleKey === "admin" ? [] : [BOTTOM_GROUP];
  const groups = new Map();
  const bottomItems = new Map(bottomNames.map((name) => [name, []]));
  const links = [];

  routes.forEach((route, order) => {
    const meta = route.meta || {};
    if (!meta.listed) {
      return;
    }
    if (meta.developer_only && !context.isDeveloper) {
      return;
    }

    const groupName = groupOf(route);
    const menuSort = asNumber(meta.menuSort);
    const fallbackSort = (order + 1) * 100;
    const inBottom = bottomNames.includes(groupName);

    if (inBottom) {
      if (!roleMenu.isChild(route)) {
        return;
      }
      bottomItems.get(groupName).push({
        route,
        order,
        sort: menuSort === null ? fallbackSort : menuSort,
      });
      return;
    }

    if (!roleMenu.isLink(route) && !roleMenu.isChild(route)) {
      return;
    }
    if (!groupName && meta.only_in_menu) {
      return;
    }
    if (groupName && !roleMenu.isChild(route)) {
      return;
    }
    if (!groupName && !roleMenu.isLink(route)) {
      return;
    }

    if (!groupName) {
      let sort = menuSort === null ? fallbackSort : menuSort;
      if (menuSort === null && roleMenu.pinTop && meta.is_top) {
        sort = -1000;
      }
      links.push({ route, order, sort });
      return;
    }

    const item = {
      route,
      order,
      sort: menuSort === null ? fallbackSort : menuSort,
    };

    if (!groups.has(groupName)) {
      const preset = defaults.get(groupName);
      groups.set(groupName, {
        name: groupName,
        order,
        sort: preset ? preset.sort : 1000 + groups.size,
        explicitSort: null,
        known: !!preset,
        icon: preset ? preset.icon : "",
        iconFromRoute: false,
        items: [],
      });
    }

    const group = groups.get(groupName);
    const groupSort = asNumber(meta.groupSort);
    if (groupSort !== null && (group.explicitSort === null || groupSort < group.explicitSort)) {
      group.explicitSort = groupSort;
      group.sort = groupSort;
    }
    if (meta.groupIcon && !group.iconFromRoute) {
      group.icon = meta.groupIcon;
      group.iconFromRoute = true;
    }
    group.items.push(item);
  });

  const groupEntries = [...groups.values()]
    .filter((group) => group.items.length && (group.known || group.explicitSort !== null || roleMenu.showUnknownGroups))
    .map((group) => ({
      type: "group",
      key: "group:" + group.name,
      name: group.name,
      icon: group.icon,
      sort: group.sort,
      order: group.order,
      items: group.items.sort(bySort).map((item) => item.route),
    }));

  const linkEntries = links.map((item) => ({
    type: "link",
    key: "link:" + (item.route.name || item.route.path),
    route: item.route,
    sort: item.sort,
    order: item.order,
  }));

  return {
    entries: [...groupEntries, ...linkEntries].sort(bySort),
    bottom: bottomNames.map((name) => ({
      type: "group",
      key: "bottom:" + name,
      name,
      icon: getSupportIcon(),
      items: (bottomItems.get(name) || []).sort(bySort).map((item) => item.route),
    })),
  };
}

/** @deprecated Prefer buildUserMenu. Admin-only wrapper. */
export function buildAdminMenu(routes, options = {}) {
  return buildUserMenu(routes, {
    isAdmin: true,
    role: "7",
    isDeveloper: options.isDeveloper,
    iconColor: options.iconColor,
  });
}
